import axios from "axios";
import Cookies from "universal-cookie";
import type {
  Club,
  CreateVenueBookingResponse,
  Handler,
  SlotLength,
  VenueAvailabilityWindow,
  VenueBlock,
  VenueBooking,
  VenueHireSettings,
  VenueSlot,
  VenueHireShareAction,
  SharedVenueInfo,
  BookOneOffSessionResponse,
  SharedOneOffSessionInfo,
} from "./types";
import { filterUpcomingVenueBookings, normalizeVenueBookings } from "./venueHireUtils";

const BASE_URL = "https://api.easyagility.co.uk";

const authHeaders = () => {
  const token = new Cookies().get("token");
  return { Authorization: "Bearer " + token };
};

export interface CreateSandboxClubRequest {
  name: string;
  first_name?: string;
  last_name?: string;
}

export interface CreateSandboxClubResponse {
  id: number;
  name: string;
  is_sandbox: boolean;
}

export const createSandboxClub = async (
  request: CreateSandboxClubRequest,
): Promise<CreateSandboxClubResponse> => {
  const { data } = await axios.post<CreateSandboxClubResponse>(
    `${BASE_URL}/create-sandbox-club`,
    request,
    { headers: authHeaders() },
  );
  return data;
};

export const fetchClubs = async (): Promise<Club[]> => {
  const { data } = await axios.get<Club[]>(`${BASE_URL}/my-clubs`, {
    headers: authHeaders(),
  });
  return data;
};

export const fetchHandlers = async (): Promise<Handler[]> => {
  const { data } = await axios.get<Handler[]>(`${BASE_URL}/handlers`, {
    headers: authHeaders(),
  });
  return data;
};

export const addHandlerToClub = async (
  clubId: number,
  handlerId: number,
): Promise<void> => {
  await axios.post(
    `${BASE_URL}/add-handler-to-club`,
    { club_id: clubId, handler_id: handlerId },
    { headers: authHeaders() },
  );
};

export const removeClubMember = async (
  clubId: number,
  handlerId: number,
): Promise<void> => {
  await axios.post(
    `${BASE_URL}/remove-club-member`,
    { club_id: clubId, handler_id: handlerId },
    { headers: authHeaders() },
  );
};

export const createTrainingGroup = async (
  clubId: number,
  name: string,
): Promise<void> => {
  await axios.post(
    `${BASE_URL}/create-training-group`,
    { club_id: clubId, name },
    { headers: authHeaders() },
  );
};

export const addTrainingGroupMember = async (
  trainingGroupId: number,
  handlerId: number,
): Promise<void> => {
  await axios.post(
    `${BASE_URL}/add-training-group-member`,
    { training_group_id: trainingGroupId, handler_id: handlerId },
    { headers: authHeaders() },
  );
};

export const removeTrainingGroupMember = async (
  trainingGroupId: number,
  handlerId: number,
): Promise<void> => {
  await axios.post(
    `${BASE_URL}/remove-training-group-member`,
    { training_group_id: trainingGroupId, handler_id: handlerId },
    { headers: authHeaders() },
  );
};

export const updateTrainingGroupDefaultFee = async (
  trainingGroupId: number,
  defaultFee: number,
  defaultFeeBasis: "invoice" | "session",
): Promise<void> => {
  await axios.post(
    `${BASE_URL}/update-training-group-default-fee`,
    {
      training_group_id: trainingGroupId,
      default_fee: defaultFee,
      default_fee_basis: defaultFeeBasis,
    },
    { headers: authHeaders() },
  );
};

export const renameTrainingGroup = async (
  trainingGroupId: number,
  name: string,
): Promise<void> => {
  await axios.post(
    `${BASE_URL}/rename-training-group`,
    { training_group_id: trainingGroupId, name },
    { headers: authHeaders() },
  );
};

export const archiveTrainingGroup = async (
  trainingGroupId: number,
): Promise<void> => {
  await axios.post(
    `${BASE_URL}/archive-training-group`,
    { training_group_id: trainingGroupId },
    { headers: authHeaders() },
  );
};

export const saveTrainingDateNotes = async (
  trainingGroupId: number,
  trainingDate: string,
  notes: string,
): Promise<void> => {
  await axios.post(
    `${BASE_URL}/training-date-notes`,
    {
      training_group_id: trainingGroupId,
      training_date: trainingDate,
      notes,
    },
    { headers: authHeaders() },
  );
};

export interface IssueInvoicesRequest {
  club_id: number;
  group_name: string;
  invoice_period_start: string;
  invoice_period_end: string;
  due_date?: string;
  training_dates: string[];
  members: Array<{
    handler_id: number;
    fee: number;
    excluded_training_dates?: string[];
  }>;
}

export const issueInvoices = async (
  request: IssueInvoicesRequest,
): Promise<void> => {
  await axios.post(`${BASE_URL}/issue-invoices`, request, {
    headers: authHeaders(),
  });
};

export const voidInvoice = async (invoiceId: number): Promise<void> => {
  await axios.post(
    `${BASE_URL}/void-invoice`,
    { invoice_id: invoiceId },
    { headers: authHeaders() },
  );
};

export interface CreateOneOffSessionRequest {
  club_id: number;
  name: string;
  session_date: string;
  notes?: string;
}

export interface CreateOneOffSessionResponse {
  id: number;
  name: string;
  session_date: string;
}

export const createOneOffSession = async (
  request: CreateOneOffSessionRequest,
): Promise<CreateOneOffSessionResponse> => {
  const { data } = await axios.post<CreateOneOffSessionResponse>(
    `${BASE_URL}/create-one-off-session`,
    request,
    { headers: authHeaders() },
  );
  return data;
};

export interface UpdateOneOffSessionRequest {
  one_off_session_id: number;
  name?: string;
  session_date?: string;
  notes?: string;
}

export const updateOneOffSession = async (
  request: UpdateOneOffSessionRequest,
): Promise<void> => {
  await axios.post(`${BASE_URL}/update-one-off-session`, request, {
    headers: authHeaders(),
  });
};

export const cancelOneOffSession = async (
  oneOffSessionId: number,
): Promise<void> => {
  await axios.post(
    `${BASE_URL}/cancel-one-off-session`,
    { one_off_session_id: oneOffSessionId },
    { headers: authHeaders() },
  );
};

export interface AddOneOffSessionParticipantRequest {
  one_off_session_id: number;
  handler_id?: number;
  email_address?: string;
  first_name?: string;
  last_name?: string;
}

export const addOneOffSessionParticipant = async (
  request: AddOneOffSessionParticipantRequest,
): Promise<{ id: number; handler_id: number }> => {
  const { data } = await axios.post<{ id: number; handler_id: number }>(
    `${BASE_URL}/add-one-off-session-participant`,
    request,
    { headers: authHeaders() },
  );
  return data;
};

export const removeOneOffSessionParticipant = async (
  oneOffSessionId: number,
  participantId: number,
): Promise<void> => {
  await axios.post(
    `${BASE_URL}/remove-one-off-session-participant`,
    {
      one_off_session_id: oneOffSessionId,
      participant_id: participantId,
    },
    { headers: authHeaders() },
  );
};

export interface IssueOneOffSessionInvoicesRequest {
  one_off_session_id: number;
  due_date?: string;
  participants: Array<{
    participant_id: number;
    fee: number;
  }>;
}

export const issueOneOffSessionInvoices = async (
  request: IssueOneOffSessionInvoicesRequest,
): Promise<void> => {
  await axios.post(`${BASE_URL}/issue-one-off-session-invoices`, request, {
    headers: authHeaders(),
  });
};

export type OneOffSessionShareAction = "enable" | "regenerate" | "disable";

export const updateOneOffSessionBooking = async (request: {
  one_off_session_id: number;
  booking_mode: "off" | "private" | "public";
  capacity?: number;
  fee: number;
}): Promise<void> => {
  await axios.post(`${BASE_URL}/update-one-off-session-booking`, request, {
    headers: authHeaders(),
  });
};

export const setOneOffSessionShare = async (
  oneOffSessionId: number,
  action: OneOffSessionShareAction,
): Promise<{ share_token: string | null }> => {
  const { data } = await axios.post<{ share_token: string | null }>(
    `${BASE_URL}/one-off-session-share`,
    { one_off_session_id: oneOffSessionId, action },
    { headers: authHeaders() },
  );
  return data;
};

export const bookOneOffSession = async (request: {
  one_off_session_id?: number;
  share_token?: string;
  quantity?: number;
}): Promise<BookOneOffSessionResponse> => {
  const { data } = await axios.post<BookOneOffSessionResponse>(
    `${BASE_URL}/book-one-off-session`,
    request,
    { headers: authHeaders() },
  );
  return data;
};

export const fetchSharedOneOffSession = async (
  token: string,
): Promise<SharedOneOffSessionInfo> => {
  const { data } = await axios.get<SharedOneOffSessionInfo>(
    `${BASE_URL}/one-off-sessions/shared/${encodeURIComponent(token)}`,
    { headers: authHeaders() },
  );
  return data;
};

export const updateVenueHireSettings = async (
  clubId: number,
  settings: VenueHireSettings,
  freeBookingCode?: string | null,
): Promise<void> => {
  await axios.post(
    `${BASE_URL}/club-venue-hire-settings`,
    {
      club_id: clubId,
      ...settings,
      free_booking_code: freeBookingCode ?? null,
    },
    { headers: authHeaders() },
  );
};

export const fetchVenueAvailability = async (
  clubId: number,
  from: string,
  to: string,
): Promise<VenueAvailabilityWindow[]> => {
  const { data } = await axios.get<VenueAvailabilityWindow[]>(
    `${BASE_URL}/club/${clubId}/venue-availability`,
    { headers: authHeaders(), params: { from, to } },
  );
  return data;
};

export const fetchVenueBlocks = async (
  clubId: number,
  from: string,
  to: string,
): Promise<VenueBlock[]> => {
  const { data } = await axios.get<VenueBlock[]>(
    `${BASE_URL}/club/${clubId}/venue-blocks`,
    { headers: authHeaders(), params: { from, to } },
  );
  return data;
};

export type CreateVenueAvailabilityInput = Omit<
  VenueAvailabilityWindow,
  "id"
> & {
  end_date?: string;
};

export const createVenueAvailability = async (
  clubId: number,
  window: CreateVenueAvailabilityInput,
): Promise<VenueAvailabilityWindow[]> => {
  const { data } = await axios.post<VenueAvailabilityWindow[]>(
    `${BASE_URL}/create-venue-availability`,
    { club_id: clubId, ...window },
    { headers: authHeaders() },
  );
  return data;
};

export const deleteVenueAvailability = async (
  windowId: number,
): Promise<void> => {
  await axios.post(
    `${BASE_URL}/delete-venue-availability`,
    { id: windowId },
    { headers: authHeaders() },
  );
};

export const createVenueBlock = async (
  clubId: number,
  block: Omit<VenueBlock, "id">,
): Promise<VenueBlock> => {
  const { data } = await axios.post<VenueBlock>(
    `${BASE_URL}/create-venue-block`,
    { club_id: clubId, ...block },
    { headers: authHeaders() },
  );
  return data;
};

export const deleteVenueBlock = async (blockId: number): Promise<void> => {
  await axios.post(
    `${BASE_URL}/delete-venue-block`,
    { id: blockId },
    { headers: authHeaders() },
  );
};

export const fetchVenueSlots = async (
  clubId: number,
  from: string,
  to: string,
): Promise<VenueSlot[]> => {
  const { data } = await axios.get<VenueSlot[]>(
    `${BASE_URL}/club/${clubId}/venue-slots`,
    { headers: authHeaders(), params: { from, to } },
  );
  return data;
};

export const fetchVenueBookings = async (
  clubId: number,
): Promise<VenueBooking[]> => {
  const { data } = await axios.get<VenueBooking[]>(
    `${BASE_URL}/club/${clubId}/venue-bookings`,
    { headers: authHeaders() },
  );
  return data;
};

export interface CreateVenueBookingRequest {
  date: string;
  start_time: string;
  end_time: string;
  slot_length: SlotLength;
  voucher_code?: string;
}

export const createVenueBooking = async (
  clubId: number,
  request: CreateVenueBookingRequest,
): Promise<CreateVenueBookingResponse> => {
  const { data } = await axios.post<CreateVenueBookingResponse>(
    `${BASE_URL}/book-venue-slot`,
    { club_id: clubId, ...request },
    { headers: authHeaders() },
  );
  return data;
};

export const cancelVenueBooking = async (bookingId: number): Promise<void> => {
  await axios.post(
    `${BASE_URL}/cancel-venue-booking`,
    { id: bookingId },
    { headers: authHeaders() },
  );
};

export const fetchMyVenueBookings = async (
  clubId: number,
): Promise<VenueBooking[]> => {
  const { data } = await axios.get<VenueBooking[]>(
    `${BASE_URL}/club/${clubId}/my-venue-bookings`,
    { headers: authHeaders() },
  );
  return filterUpcomingVenueBookings(normalizeVenueBookings(data));
};

export interface RescheduleVenueBookingRequest {
  date: string;
  start_time: string;
  end_time: string;
  slot_length: SlotLength;
}

export const rescheduleVenueBooking = async (
  bookingId: number,
  request: RescheduleVenueBookingRequest,
): Promise<VenueBooking> => {
  const { data } = await axios.post<VenueBooking>(
    `${BASE_URL}/reschedule-venue-booking`,
    { id: bookingId, ...request },
    { headers: authHeaders() },
  );
  const [booking] = normalizeVenueBookings([data]);
  if (!booking) {
    throw new Error("Invalid booking response");
  }
  return booking;
};

export const setVenueHireShare = async (
  clubId: number,
  action: VenueHireShareAction,
): Promise<{ share_token: string | null }> => {
  const { data } = await axios.post<{ share_token: string | null }>(
    `${BASE_URL}/club-venue-hire-share`,
    { club_id: clubId, action },
    { headers: authHeaders() },
  );
  return data;
};

export const fetchSharedVenueInfo = async (
  token: string,
): Promise<SharedVenueInfo> => {
  const { data } = await axios.get<SharedVenueInfo>(
    `${BASE_URL}/venue-hire/shared/${encodeURIComponent(token)}`,
    { headers: authHeaders() },
  );
  return data;
};

export const fetchSharedVenueSlots = async (
  token: string,
  from: string,
  to: string,
): Promise<VenueSlot[]> => {
  const { data } = await axios.get<VenueSlot[]>(
    `${BASE_URL}/venue-hire/shared/${encodeURIComponent(token)}/slots`,
    { headers: authHeaders(), params: { from, to } },
  );
  return data;
};

export interface CreateSharedVenueBookingRequest extends CreateVenueBookingRequest {
  first_name?: string;
  last_name?: string;
}

export const createSharedVenueBooking = async (
  token: string,
  request: CreateSharedVenueBookingRequest,
): Promise<CreateVenueBookingResponse> => {
  const { data } = await axios.post<CreateVenueBookingResponse>(
    `${BASE_URL}/venue-hire/shared/${encodeURIComponent(token)}/book`,
    request,
    { headers: authHeaders() },
  );
  return data;
};
